import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Truck, CheckCircle2, Clock, AlertTriangle, Package, ChevronDown, Loader2, MapPin } from 'lucide-react';
import trackingService, { Shipment, TrackEvent } from '@/services/tracking.service';
import { formatDateLong, formatDayTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const fmt = (s?: string | null) => {
  if (!s) return '';
  try { return formatDayTime(s); } catch { return ''; }
};
const fmtDay = (s?: string | null) => {
  if (!s) return '';
  try { return formatDateLong(s); } catch { return ''; }
};

// 12 Universal Event → renk/ikon
function eventStyle(code: string, name = '') {
  const normalizedCode = String(code || '').toUpperCase();
  const normalizedName = String(name || '').toLocaleLowerCase('de');

  if (normalizedName.includes('zugestellt') || normalizedName.includes('delivered')) {
    return { dot: 'bg-emerald-500', text: 'text-emerald-700', Icon: CheckCircle2 };
  }

  switch (normalizedCode) {
    case 'DELIVERED':
    case 'DELIVERED_TO_NEIGHBOR':
    case 'DELIVERED_TO_SAFE_PLACE':
      return { dot: 'bg-emerald-500', text: 'text-emerald-700', Icon: CheckCircle2 };
    case 'OUT_FOR_DELIVERY':
      return { dot: 'bg-blue-500', text: 'text-blue-700', Icon: Truck };
    case 'IN_TRANSIT':
    case 'CUSTOMS_PROCESSING':
      return { dot: 'bg-blue-400', text: 'text-blue-600', Icon: Truck };
    case 'DELAYED':
    case 'DELIVERY_FAILED':
    case 'ADDRESS_ISSUE':
    case 'RETURNED':
      return { dot: 'bg-amber-500', text: 'text-amber-700', Icon: AlertTriangle };
    case 'READY_FOR_PICKUP':
      return { dot: 'bg-indigo-500', text: 'text-indigo-700', Icon: Package };
    default:
      return { dot: 'bg-slate-400', text: 'text-slate-600', Icon: Clock };
  }
}

function ShipmentCard({ s }: { s: Shipment }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const events: TrackEvent[] = [...(s.timeline || [])].sort((a, b) => {
    const aTime = Date.parse(a.timestamp || '') || 0;
    const bTime = Date.parse(b.timestamp || '') || 0;
    return bTime - aTime;
  });
  const shown = showAll ? events : events.slice(0, 3);
  const top = events[0];
  const phaseStyle = top ? eventStyle(top.code, top.name) : eventStyle('');
  const PhaseIcon = phaseStyle.Icon;
  const isDelivered = !!top && (
    ['DELIVERED', 'DELIVERED_TO_NEIGHBOR', 'DELIVERED_TO_SAFE_PLACE'].includes(String(top.code || '').toUpperCase())
    || String(top.name || '').toLocaleLowerCase('de').includes('zugestellt')
    || String(top.name || '').toLocaleLowerCase('de').includes('delivered')
  );
  const deliveryLabel = s.deliveredAt
    ? `${t('common:x4.delivered2', 'Zugestellt')} ${fmtDay(s.deliveredAt)}`
    : isDelivered && top
      ? `${t('common:x4.delivered2', 'Zugestellt')} ${fmtDay(top.timestamp)}`
      : s.estimatedDelivery
        ? `ETA ${fmtDay(s.estimatedDelivery)}`
        : '';

  return (
    <div className="tracking-group">
      <button
        onClick={() => setExpanded((o) => !o)}
        className="tracking-summary"
        aria-expanded={expanded}
      >
        <span className="tracking-carrier">
          <strong>{s.carrier}</strong>
          {s.trackingNumber && <span>{s.trackingNumber}</span>}
        </span>
        <span className="tracking-current">
          <span className={`tracking-state ${isDelivered ? 'tracking-state--delivered' : ''}`}>
            <PhaseIcon className="h-3 w-3" />
            {top?.name || s.currentPhase}
          </span>
          {deliveryLabel && (
            <span className="tracking-date">
              {deliveryLabel}
            </span>
          )}
        </span>
        <ChevronDown className={`tracking-chevron ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (events.length > 0 ? (
        <div className="tracking-events">
          <ol className="tracking-event-list">
            {shown.map((e, i) => {
              const st = eventStyle(e.code, e.name);
              const EventIcon = st.Icon;
              return (
                <li key={`${e.code}-${e.timestamp}-${i}`} className={`tracking-event ${i === 0 ? 'tracking-event--current' : ''}`}>
                  <span className="tracking-event-rail" aria-hidden="true">
                    <span className="tracking-event-dot">
                      <EventIcon className="h-3 w-3" />
                    </span>
                  </span>
                  <span className="tracking-event-body">
                    <span className="tracking-event-topline">
                      <strong>{e.name}</strong>
                      <time>{fmt(e.timestamp)}</time>
                    </span>
                    <span className="tracking-event-subline">
                      {e.message && <span>{e.message}</span>}
                      {e.location && <span className="tracking-location"><MapPin className="h-2.5 w-2.5" />{e.location}</span>}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
          {events.length > 3 && (
            <button onClick={() => setShowAll((o) => !o)} className="tracking-more">
              {showAll ? 'Weniger anzeigen' : `${events.length - 3} weitere Ereignisse`}
              <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      ) : (
        <div className="tracking-empty">{t('agent:drafts.noEvents', 'Noch keine Ereignisse')}</div>
      ))}
    </div>
  );
}

/**
 * Sendungsverfolgung (trackba). email+order VEYA trackingNumber ile. JTL/Shopify fark etmez.
 * Veri yoksa hiçbir şey render etmez (panelde boş yer kaplamaz).
 */
export default function TrackingTimeline({ email, orderNumber, trackingNumber, carrier }: { email?: string; orderNumber?: string; trackingNumber?: string; carrier?: string }) {
  const { t } = useTranslation();
  const enabled = !!(trackingNumber || (email && orderNumber));
  const { data, isLoading } = useQuery({
    queryKey: ['tracking', email, orderNumber, trackingNumber, carrier],
    queryFn: () => trackingService.lookup({ email, order: orderNumber, trackingNumber, carrier }),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!enabled) return null;
  if (isLoading) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
        <Loader2 className="h-3 w-3 animate-spin" /> {t('common:x4.trackingLoading', 'Sendungsverfolgung wird geladen…')}
      </div>
    );
  }
  const shipments = data?.shipments || [];
  if (!data?.found || shipments.length === 0) return null;

  return (
    <div className="tracking-section">
      <div className="tracking-section-title">
        <Truck className="h-3.5 w-3.5" /> {t('common:x4.tracking', 'Sendungsverfolgung')}
      </div>
      {shipments.map((s, i) => <ShipmentCard key={i} s={s} />)}
    </div>
  );
}
